"use client";

import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { calcAll, type TaxResult } from "../../utils/taxCalculation";

// Landing Page Font Stack
const FONT_SANS =
  '"Avant Garde", "Century Gothic", "Futura", -apple-system, BlinkMacSystemFont, sans-serif';
const FONT_MONO =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Mono", monospace';

// Landing Page Farben - nur 2 Hauptfarben + Bitcoin
const COLOR_ACCENT = "#E85D3B"; // Staat / Negativ
const COLOR_BASE = "#b7ab98"; // Neutral / Positiv
const COLOR_BITCOIN = "#f7931a"; // Bitcoin Orange

// Anzahl Tage im Jahr - wird in Beschriftung und Steuer-Logik verwendet.
const DAYS_PER_YEAR = 365;
// Anzahl Punkte im Ring-Diagramm - dichter als 365 für mehr visuelle Detailfülle.
// Punkte werden proportional zum Steuer-Anteil eingefärbt (taxDays / 365 * DOT_COUNT).
const DOT_COUNT = 730;

interface DotPosition {
  x: number;
  y: number;
  r: number;
  sortKey: number;
}

// Ring-Layout berechnen
function buildRingLayout(canvasSize: number) {
  const dotDiameter = 7;
  const ringGap = 2.5;
  const dotGapMin = 1.8;
  const outerRadius = (canvasSize / 340) * 152;
  const ringStep = dotDiameter + ringGap;

  const rings: { radius: number; count: number; dotR: number }[] = [];
  let remaining = DOT_COUNT;
  let r = outerRadius;

  while (remaining > 0 && r > (canvasSize / 340) * 50) {
    const circumference = 2 * Math.PI * r;
    const slot = dotDiameter + dotGapMin;
    let count = Math.floor(circumference / slot);
    count = Math.min(count, remaining);
    rings.push({ radius: r, count, dotR: dotDiameter / 2 });
    remaining -= count;
    r -= ringStep;
  }

  if (remaining > 0 && rings.length > 0) {
    const lastRing = rings[rings.length - 1];
    if (lastRing) lastRing.count += remaining;
  }

  return rings;
}

// Punkt-Positionen berechnen (spiralförmig sortiert)
function buildDotPositions(canvasSize: number): DotPosition[] {
  const cx = canvasSize / 2;
  const cy = canvasSize / 2;
  const rings = buildRingLayout(canvasSize);

  const allDotsUnsorted: DotPosition[] = [];

  rings.forEach((ring, ringIdx) => {
    for (let i = 0; i < ring.count; i++) {
      const angle = -Math.PI / 2 + (i / ring.count) * Math.PI * 2;
      let normalizedAngle = (angle + Math.PI / 2) / (Math.PI * 2);
      if (normalizedAngle < 0) normalizedAngle += 1;
      const ringOffset = ringIdx * 0.001;

      allDotsUnsorted.push({
        x: cx + ring.radius * Math.cos(angle),
        y: cy + ring.radius * Math.sin(angle),
        r: ring.dotR,
        sortKey: normalizedAngle + ringOffset,
      });
    }
  });

  return allDotsUnsorted.sort((a, b) => a.sortKey - b.sortKey);
}

interface TaxCounterProps {
  activeIndex?: number;
  className?: string;
  // Wenn true: links Ring/Legende, rechts Gehalt+Slider+Cards+Breakeven.
  // Default: vertikaler Single-Column-Stack (Legacy-Verhalten).
  splitLayout?: boolean;
}

export function TaxCounter({ className = "", splitLayout = false }: TaxCounterProps) {
  const [bruttoMonat, setBruttoMonat] = useState(3500);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number | null>(null);
  const animTaxDays = useRef(145);
  const animPercent = useRef(39.6);

  // Responsive
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth <= 1024;
  const isLargeDesktop = windowWidth >= 2400;

  // Im splitLayout hat die linke Spalte deutlich mehr Platz - Canvas größer.
  const canvasSize = isMobile
    ? 260
    : splitLayout
    ? isTablet
      ? 380
      : isLargeDesktop
      ? 600
      : 520
    : isTablet
    ? 300
    : isLargeDesktop
    ? 380
    : 340;

  // Berechnung
  const taxResult = useMemo(() => calcAll(bruttoMonat), [bruttoMonat]);

  // Dot-Positionen
  const dots = useMemo(() => buildDotPositions(canvasSize), [canvasSize]);

  // Canvas-Rendering
  const renderRing = useCallback(
    (ctx: CanvasRenderingContext2D, taxDays: number, percentage: number) => {
      const S = canvasSize;
      const cx = S / 2;
      const cy = S / 2;
      ctx.clearRect(0, 0, S, S);

      // Punkte proportional einfärben: taxDays von 365 ergibt den Anteil,
      // mal DOT_COUNT für die tatsächliche Anzahl orangener Punkte.
      const taxCount = Math.round((taxDays / DAYS_PER_YEAR) * DOT_COUNT);

      dots.forEach((dot, i) => {
        const isTax = i < taxCount;
        ctx.beginPath();
        ctx.arc(dot.x, dot.y, dot.r, 0, Math.PI * 2);

        if (isTax) {
          ctx.fillStyle = COLOR_ACCENT;
          ctx.shadowColor = "rgba(232,93,59,0.35)";
          ctx.shadowBlur = 5;
        } else {
          ctx.fillStyle = "rgba(183,171,152,0.55)";
          ctx.shadowColor = "transparent";
          ctx.shadowBlur = 0;
        }

        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // Center-Texte
      ctx.fillStyle = COLOR_ACCENT;
      ctx.font = `800 ${S * 0.1}px ${FONT_MONO}`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(percentage.toFixed(1) + "%", cx, cy - S * 0.05);

      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.font = `500 ${S * 0.026}px ${FONT_SANS}`;
      ctx.fillText("DEINER LEBENSZEIT", cx, cy + S * 0.02);

      ctx.fillStyle = COLOR_ACCENT;
      ctx.font = `600 ${S * 0.029}px ${FONT_SANS}`;
      ctx.fillText("FÜR DEN STAAT", cx, cy + S * 0.065);

      ctx.fillStyle = "rgba(255,255,255,0.2)";
      ctx.font = `600 ${S * 0.026}px ${FONT_MONO}`;
      ctx.fillText(
        Math.round(taxDays) + " von " + DAYS_PER_YEAR + " Tagen",
        cx,
        cy + S * 0.12
      );
    },
    [dots, canvasSize]
  );

  // Animation Loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvasSize * dpr;
    canvas.height = canvasSize * dpr;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const targetTaxDays = taxResult.steuerTageImJahr;
    const targetPercent = taxResult.abgabenquote;

    const loop = () => {
      const speed = 0.08;
      animTaxDays.current += (targetTaxDays - animTaxDays.current) * speed;
      animPercent.current += (targetPercent - animPercent.current) * speed;

      if (Math.abs(animTaxDays.current - targetTaxDays) < 0.1)
        animTaxDays.current = targetTaxDays;
      if (Math.abs(animPercent.current - targetPercent) < 0.05)
        animPercent.current = targetPercent;

      renderRing(ctx, animTaxDays.current, animPercent.current);

      // Weiterlaufen solange Animation nicht fertig
      if (
        Math.abs(animTaxDays.current - targetTaxDays) > 0.05 ||
        Math.abs(animPercent.current - targetPercent) > 0.05
      ) {
        animRef.current = requestAnimationFrame(loop);
      } else {
        // Einmal final rendern
        renderRing(ctx, targetTaxDays, targetPercent);
        animRef.current = null;
      }
    };

    // Animation starten
    if (animRef.current) cancelAnimationFrame(animRef.current);
    animRef.current = requestAnimationFrame(loop);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [taxResult, canvasSize, renderRing]);

  const sliderFillPercent =
    ((bruttoMonat - 1500) / (8000 - 1500)) * 100;

  function fmtEuro(val: number): string {
    return Math.round(val).toLocaleString("de-DE") + " EUR";
  }

  // Custom Drag-Logic für den Slider mit zwei Modi:
  // - "absolute": Klick direkt auf den Track -> Wert springt zur Klick-Position
  // - "delta":    Klick im Snap-Zustand (Maske am Thumb, Maus oft daneben) ->
  //               Wert wird relativ zur Maus-Bewegung verschoben, der Thumb
  //               springt nicht zur falschen Maus-Position
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const dragModeRef = useRef<"absolute" | "delta">("absolute");
  const dragStartXRef = useRef(0);
  const dragStartValueRef = useRef(3500);
  // bruttoMonat als Ref (kein Re-Render des useEffect bei jedem Slider-Update).
  const bruttoMonatRef = useRef(bruttoMonat);
  useEffect(() => {
    bruttoMonatRef.current = bruttoMonat;
  }, [bruttoMonat]);

  const updateFromClientX = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const value = 1500 + ratio * (8000 - 1500);
    const snapped = Math.round(value / 100) * 100;
    setBruttoMonat(snapped);
  }, []);

  const updateFromDelta = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const deltaX = clientX - dragStartXRef.current;
    const deltaValue = (deltaX / rect.width) * (8000 - 1500);
    const raw = dragStartValueRef.current + deltaValue;
    const clamped = Math.max(1500, Math.min(8000, raw));
    setBruttoMonat(Math.round(clamped / 100) * 100);
  }, []);

  useEffect(() => {
    // Globaler pointerdown: wenn die Maske am Slider-Thumb gesnappt ist
    // (gesetzt vom MaskCursor via body[data-mask-snapped="slider-thumb"]),
    // startet Drag im Delta-Modus - egal wo die echte Maus liegt.
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      const onTrack = trackRef.current?.contains(target ?? null) ?? false;
      // Track-Klick wird von onTrackPointerDown gehandhabt (absolute Modus).
      if (onTrack) return;
      const snapped =
        document.body.getAttribute("data-mask-snapped") === "slider-thumb";
      if (!snapped) return;
      e.preventDefault();
      draggingRef.current = true;
      dragModeRef.current = "delta";
      dragStartXRef.current = e.clientX;
      dragStartValueRef.current = bruttoMonatRef.current;
      document.body.style.userSelect = "none";
      // Cursor global ausschalten - selbst wenn die Maus während Drag über
      // Elemente wandert die ihren eigenen cursor setzen, bleibt der native
      // Pointer unsichtbar.
      document.documentElement.style.cursor = "none";
      // Signalisiert dem MaskCursor: Maske soll permanent auf den Slider-Thumb
      // gesnappt bleiben (egal wo die Maus ist), sodass die orange Maske mit
      // dem Thumb mitwandert.
      document.body.setAttribute("data-slider-dragging", "true");
    };

    const onMove = (e: PointerEvent) => {
      if (!draggingRef.current) return;
      e.preventDefault();
      if (dragModeRef.current === "delta") {
        updateFromDelta(e.clientX);
      } else {
        updateFromClientX(e.clientX);
      }
    };

    const onUp = () => {
      if (draggingRef.current) {
        draggingRef.current = false;
        document.body.style.userSelect = "";
        document.documentElement.style.cursor = "";
        document.body.removeAttribute("data-slider-dragging");
      }
    };

    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };
  }, [updateFromClientX, updateFromDelta]);

  const onTrackPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    dragModeRef.current = "absolute";
    document.body.style.userSelect = "none";
    document.documentElement.style.cursor = "none";
    document.body.setAttribute("data-slider-dragging", "true");
    updateFromClientX(e.clientX);
  };

  // Sub-Sections als JSX-Variablen, damit sie je nach Layout (single/split)
  // unterschiedlich angeordnet werden können.
  const gehaltDisplay = (
    <div style={{ textAlign: "center", width: "100%" }}>
      <div
        style={{
          fontFamily: FONT_SANS,
          fontSize: 11,
          letterSpacing: 1.5,
          textTransform: "uppercase" as const,
          fontWeight: 600,
          color: "rgba(255,255,255,0.4)",
          marginBottom: 4,
        }}
      >
        Dein Bruttogehalt
      </div>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 18,
          fontWeight: 800,
          color: COLOR_BASE,
        }}
      >
        {bruttoMonat.toLocaleString("de-DE")} EUR
      </div>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 11,
          fontWeight: 600,
          color: "rgba(255,255,255,0.3)",
          marginTop: 2,
        }}
      >
        = {(bruttoMonat * 12).toLocaleString("de-DE")} EUR / Jahr
      </div>
    </div>
  );

  const sliderRow = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        width: "100%",
      }}
    >
      <span
        style={{
          fontSize: 9,
          color: "rgba(255,255,255,0.2)",
          fontFamily: FONT_MONO,
          flexShrink: 0,
        }}
      >
        1.500
      </span>
      <div
        ref={trackRef}
        onPointerDown={onTrackPointerDown}
        role="slider"
        tabIndex={0}
        aria-label="Bruttogehalt pro Monat"
        aria-valuemin={1500}
        aria-valuemax={8000}
        aria-valuenow={bruttoMonat}
        style={{
          position: "relative",
          flex: 1,
          height: 30,
          display: "flex",
          alignItems: "center",
          // KEIN cursor: pointer - das würde den nativen Mauszeiger einblenden
          // (rfx-v2-root hat cursor: none global). Der Cursor wird durch die
          // orange Maske dargestellt, nicht durch den nativen Pointer.
          cursor: "none",
          touchAction: "none",
        }}
      >
        {/* Track-Background */}
        <div
          style={{
            position: "absolute",
            left: 0,
            width: "100%",
            height: 4,
            borderRadius: 2,
            background: "rgba(255,255,255,0.08)",
            pointerEvents: "none",
          }}
        />
        {/* Track-Fill */}
        <div
          style={{
            position: "absolute",
            left: 0,
            width: `${sliderFillPercent}%`,
            height: 4,
            borderRadius: 2,
            background: `linear-gradient(to right, ${COLOR_BASE}, ${COLOR_ACCENT})`,
            pointerEvents: "none",
          }}
        />
        {/* Thumb - exakt so groß wie die NORMAL-Cursor-Maske (30 px), sodass
            beim Snap die Maske passgenau über dem Thumb liegt (visuell
            verschmelzen sie). data-mask-no-pull deaktiviert den Magnetic-Pull,
            damit die Maus den Thumb nicht zu sich zieht (sonst Wackeln). */}
        <div
          data-mask-snap-target="slider-thumb"
          data-mask-no-pull=""
          style={{
            position: "absolute",
            left: `calc(${sliderFillPercent}% - 15px)`,
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: COLOR_BASE,
            border: "2px solid #0d0d0d",
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      </div>
      <span
        style={{
          fontSize: 9,
          color: "rgba(255,255,255,0.2)",
          fontFamily: FONT_MONO,
          flexShrink: 0,
        }}
      >
        8.000
      </span>
    </div>
  );

  const ringSection = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: canvasSize, height: canvasSize }}
      />
      <div
        style={{
          display: "flex",
          gap: 24,
          marginTop: 10,
          fontSize: 11,
          color: "rgba(255,255,255,0.45)",
          fontFamily: FONT_SANS,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: COLOR_ACCENT,
            }}
          />
          Staat ({Math.round(taxResult.steuerTageImJahr)} Tage)
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: COLOR_BASE,
            }}
          />
          Für dich ({Math.round(365 - taxResult.steuerTageImJahr)} Tage)
        </div>
      </div>
    </div>
  );

  const metricCards = (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
        gap: 8,
        width: "100%",
      }}
    >
      <MetricCard
        label="Tage pro Jahr für den Staat"
        value={String(Math.round(taxResult.steuerTageImJahr))}
        sub={`= ${(taxResult.steuerTageImJahr / 30).toFixed(1)} Monate pro Jahr`}
      />
      <MetricCard
        label="Der Staat kassiert"
        value={`${taxResult.staatProTag.toFixed(2)} EUR`}
        sub="pro Tag - egal ob du arbeitest oder schläfst"
      />
      <MetricCard
        label="Steuerfrei ab"
        value={taxResult.taxFreedomDay}
        sub="Ab diesem Tag verdienst du für dich"
      />
    </div>
  );

  const breakevenBox = (
    <div
      style={{
        width: "100%",
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 12,
        padding: "16px 18px",
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: 1.5,
          textTransform: "uppercase" as const,
          fontWeight: 600,
          color: "rgba(183,171,152,0.6)",
          textAlign: "center",
          marginBottom: 14,
          fontFamily: FONT_SANS,
        }}
      >
        Um deine Steuerlast auszugleichen, brauchst du
      </div>
      <BreakevenRow
        color={COLOR_BITCOIN}
        label="Bitcoin"
        note="(> 1 Jahr)"
        amount={fmtEuro(taxResult.breakevenBtc)}
        tag="steuerfrei"
        tagType="bitcoin"
        isMobile={isMobile}
      />
      <BreakevenRow
        color={COLOR_ACCENT}
        label="Aktien-ETF"
        amount={fmtEuro(taxResult.breakevenEtf)}
        tag="18,5% Steuer"
        tagType="red"
        isMobile={isMobile}
      />
      <BreakevenRow
        color={COLOR_ACCENT}
        label="Einzelaktien"
        amount={fmtEuro(taxResult.breakevenAktien)}
        tag="26,4% Steuer"
        tagType="red"
        isMobile={isMobile}
        isLast
      />
      <div
        style={{
          fontSize: 10,
          color: "rgba(255,255,255,0.2)",
          textAlign: "center",
          marginTop: 10,
          paddingTop: 10,
          borderTop: "1px solid rgba(255,255,255,0.05)",
          fontFamily: FONT_SANS,
        }}
      >
        Gewinn nötig, um alle{" "}
        {Math.round(taxResult.steuerTageImJahr)} Steuer-Tage zurückzuholen
      </div>
    </div>
  );

  // Kein separates Slider-Style-Snippet mehr nötig - die Drag-Logik wird
  // direkt am Track-Container über onPointerDown ausgelöst.
  const sliderStyles = null;

  // Split-Layout: links der Ring, rechts Gehalt+Slider+Cards+Breakeven.
  // Auf Mobile fällt die Komponente immer auf den vertikalen Single-Stack zurück.
  if (splitLayout && !isMobile) {
    return (
      <div
        className={className}
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: isTablet ? 32 : 56,
          alignItems: "start",
        }}
      >
        {sliderStyles}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            position: "sticky",
            top: 80,
          }}
        >
          {ringSection}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 20,
            width: "100%",
          }}
        >
          {gehaltDisplay}
          {sliderRow}
          {metricCards}
          {breakevenBox}
        </div>
      </div>
    );
  }

  // Default Single-Column (Legacy-Verhalten)
  return (
    <div
      className={`flex flex-col items-center gap-4 ${className}`}
      style={{ maxWidth: isMobile ? "100%" : 480, width: "100%" }}
    >
      {sliderStyles}
      {gehaltDisplay}
      {sliderRow}
      {ringSection}
      {metricCards}
      {breakevenBox}
    </div>
  );
}

// Kennzahlen-Karte: Feste Label-Höhe damit Werte immer auf gleicher Linie
function MetricCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div
      style={{
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 12,
        padding: "12px 14px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          fontSize: 10,
          letterSpacing: 2,
          fontWeight: 600,
          textTransform: "uppercase" as const,
          color: "rgba(183,171,152,0.6)",
          fontFamily: FONT_SANS,
          height: 28,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontWeight: 800,
          fontSize: 22,
          color: COLOR_ACCENT,
          marginTop: 4,
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 11,
          color: "rgba(255,255,255,0.3)",
          marginTop: 2,
          fontFamily: FONT_SANS,
        }}
      >
        {sub}
      </div>
    </div>
  );
}

// Breakeven-Zeile
function BreakevenRow({
  color,
  label,
  note,
  amount,
  tag,
  tagType,
  isMobile,
  isLast,
}: {
  color: string;
  label: string;
  note?: string;
  amount: string;
  tag: string;
  tagType: "green" | "red" | "bitcoin";
  isMobile: boolean;
  isLast?: boolean;
}) {
  const isBitcoin = tagType === "bitcoin";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        padding: "10px 0",
        borderBottom: isLast
          ? "none"
          : "1px solid rgba(255,255,255,0.04)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12,
          fontWeight: 600,
          color: "rgba(255,255,255,0.5)",
          minWidth: isMobile ? 90 : 120,
          fontFamily: FONT_SANS,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: color,
            flexShrink: 0,
          }}
        />
        {label}
        {note && (
          <span
            style={{
              fontSize: 9,
              fontWeight: 400,
              color: "rgba(255,255,255,0.25)",
            }}
          >
            {note}
          </span>
        )}
      </div>
      <div
        style={{
          fontFamily: FONT_MONO,
          fontSize: 14,
          fontWeight: 800,
          color: color,
          textAlign: "right",
          flex: 1,
        }}
      >
        {amount}
      </div>
      <div
        style={{
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: 0.5,
          padding: "2px 6px",
          borderRadius: 4,
          textTransform: "uppercase" as const,
          minWidth: 68,
          textAlign: "center",
          background: isBitcoin
            ? "rgba(247,147,26,0.1)"
            : "rgba(232,93,59,0.06)",
          color: isBitcoin ? COLOR_BITCOIN : "rgba(232,93,59,0.5)",
          border: isBitcoin
            ? "1px solid rgba(247,147,26,0.2)"
            : "1px solid rgba(232,93,59,0.1)",
        }}
      >
        {tag}
      </div>
    </div>
  );
}
